import DialogModal from 'src/components/ui/dialogModal';
import Form from './Form/Form';
import { useLocalization } from 'src/utils/useLocalization';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';

import Dropdown from 'src/components/ui/dropdownMenu/dropdownMenu';

import { DealersService } from 'src/services/dealers.service';
import FieldUserList from './FieldUserList';
import { HttpStatusCode } from 'src/utils/enums/http-method.enum';
import { IUser } from 'src/utils/interfaces/user';
import { MODAL_CONTROL_ACTIONS } from 'src/utils/enums/user-store-actions.enum';
import ProfileOptionsIcon from 'src/components/dealer-mgmt/profile-options-icon';
import Spinner from 'src/components/ui/spinner';
import {
    DealerManagementService,
    ISendPasswordResetLinkPayload,
} from 'src/services/dealer-management.service';
import { useDispatch, useSelector } from 'react-redux';
import './styles.scss';

const MenuItem = ({
    deleteHandler,
    sendPasswordResetHandler,
    resetMFAHandler,
}: {
    deleteHandler: Function;
    sendPasswordResetHandler?: Function;
    resetMFAHandler?: Function;
}) => {
    const { dealerMgmt, editUser } = useLocalization();
    return (
        <>
            {sendPasswordResetHandler && (
                <li
                    className={'menu-items'}
                    onClick={() => {
                        sendPasswordResetHandler();
                    }}
                >
                    <span>{editUser.BUTTON_RESET_PWD_LABEL}</span>
                </li>
            )}
            {resetMFAHandler && (
                <li
                    className={'menu-items'}
                    onClick={() => {
                        resetMFAHandler();
                    }}
                >
                    <span>{dealerMgmt.DEALER_MGMT_TABLE_MENU_DELETEMFA}</span>
                </li>
            )}
            <li
                className={'menu-items'}
                onClick={() => {
                    deleteHandler();
                }}
            >
                <span style={{ color: 'rgb(228, 0, 44)' }}>
                    {dealerMgmt.DEALER_MGMT_TABLE_MENU_DELETE}
                </span>
            </li>
        </>
    );
};
interface IBodyProps {
    setSuccessNotification: Dispatch<SetStateAction<boolean>>;
    setErrorNotification: Dispatch<SetStateAction<boolean>>;
    setSuccessNotificationMessage: Dispatch<SetStateAction<string>>;
    setErrorNotificationMessage: Dispatch<SetStateAction<string>>;
    setIsSuccessDeleted: Dispatch<SetStateAction<boolean>>;
    regions: Array<any>;
    districts: Array<any>;
    isLoadingDistAndReg: boolean;
    isEditMode: boolean;
}
export interface IUserData {
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
    partyId: number;
    userId: string;
    userStatus: number;
    regions: Array<any>;
    districts: Array<any>;
    isUserLocked: boolean;
}

const Body: React.FC<IBodyProps> = ({
    setSuccessNotification,
    setErrorNotification,
    setSuccessNotificationMessage,
    setErrorNotificationMessage,
    setIsSuccessDeleted,
    regions,
    districts,
    isLoadingDistAndReg,
    isEditMode,
}) => {
    const { addNewUser, dealerMgmt, editUser } = useLocalization();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [openDrop, setOpenDrop] = useState<boolean>(false);
    const [selectedUser, setSelectedUser] = useState<any>(null);
    const [userData, setUserData] = useState<IUserData | null>(null);
    const dispatch = useDispatch();
    const [modalOpen, setModalOpen] = useState<string>('');
    const [modalIsLoading, setModalIsLoading] = useState<boolean>(false);
    const [isUserActive, setIsUserActive] = useState<boolean>(false);

    const [isFieldUsersLoading, setIsFieldUsersLoading] = useState(false);
    const [fieldUsers, setFieldUsers] = useState<Array<any>>([]);

    async function getAllFieldUsers() {
        const service = new DealerManagementService();
        try {
            setIsFieldUsersLoading(true);
            const res = await service.getAllFieldUsers();
            if (res?.data) {
                const result = res.data.map((user: any) => {
                    return {
                        id: user.email,
                        name: `${user.firstName} ${user.lastName}`,
                    };
                });
                setFieldUsers(result);
                if (selectedUser) {
                    const user = result.find(
                        (e: any) => e.id === selectedUser.id,
                    );
                    user && setSelectedUser(user);
                }
            }
            setIsFieldUsersLoading(false);
        } catch (error) {
            setIsFieldUsersLoading(false);
            console.error(error);
        }
    }

    const handleModalCancel = () => {
        setModalOpen('');
    };

    const dealerId = useSelector((state: IUser) => {
        return state.currentDealers?.default ?? '';
    });

    async function getDealerUserData() {
        setIsLoading(true);
        const service = new DealersService();
        try {
            const res = await service.getUserData(selectedUser?.id, dealerId);
            setIsLoading(false);
            if (isEditMode && res?.data) {
                const user = mapUserDataResponse(res.data);
                setUserData(user);
                setIsUserActive(isStatusActive(user.userStatus));
            }
        } catch (error) {
            setUserData(null);
            setIsLoading(false);
            setErrorNotification(true);
        }
    }

    function deleteSuccessHandler() {
        setIsLoading(false);
        dispatch({
            type: MODAL_CONTROL_ACTIONS.SET_INACTIVE,
        });
        setIsSuccessDeleted(true);
    }
    const onDeleteUserHanlder = async () => {
        setIsLoading(true);
        const service = new DealerManagementService();
        if (userData) {
            try {
                await service.deleteUser(userData.userId, userData.email);
                deleteSuccessHandler();
            } catch (error) {
                setIsLoading(false);
                setErrorNotification(true);
            }
        }
    };

    const deleteConfirm = () => {
        setModalIsLoading(true);
        onDeleteUserHanlder()
            .then(() => {
                setModalOpen('');
                resetUpdateUser();
                setModalIsLoading(false);
            })
            .catch(() => {
                setModalIsLoading(false);
            })
            .finally(() => {
                setModalIsLoading(false);
                handleModalCancel();
            });
    };

    const deleteMFAConfirm = () => {
        setModalIsLoading(true);
        onResetMFAHandler();
    };

    const onSendPasswordResetLinkHandler = () => {
        setModalOpen('PASSWORD_RESET');
    };

    const onResetMFAHandler = async () => {
        const service = new DealerManagementService();
        try {
            await service.resetMfa(userData?.email || '');
            setModalOpen('');
            setSuccessNotificationMessage(
                dealerMgmt.DEALER_MGMT_TABLE_USER_MESSAGES_SUCCESSMFA_DELETED,
            );
            setSuccessNotification(true);
            setModalIsLoading(false);
            setOpenDrop(false);
        } catch (error) {
            setModalOpen('');
            setErrorNotificationMessage(editUser.NOTIFICATION_ERROR_REQUEST);
            setErrorNotification(true);
            setModalIsLoading(false);
        }
    };

    const onSendResetMFALinkHandler = () => {
        setModalOpen('DELETE_MFA');
    };

    const passwordResetConfirm = async () => {
        setModalIsLoading(true);
        const service = new DealerManagementService();
        if (userData) {
            const request = { userId: userData.userId };
            try {
                await service.sendPasswordResetLink(
                    request as ISendPasswordResetLinkPayload,
                );
                setSuccessNotificationMessage(
                    editUser.NOTIFICATION_SUCCESS_PASSWORDRESETLINK,
                );
                setSuccessNotification(true);
                setModalIsLoading(false);
                setModalOpen('');
                setOpenDrop(false);
            } catch (error: any) {
                const message =
                    error.response?.status == HttpStatusCode.LOCKED
                        ? editUser.NOTIFICATION_ERROR_PASSWORDRESETLINK_LOCKED
                        : editUser.NOTIFICATION_ERROR_REQUEST;
                setErrorNotificationMessage(message);
                setErrorNotification(true);
                setModalIsLoading(false);
                setModalOpen('');
            }
        }
    };

    useEffect(() => {
        if (selectedUser) {
            getDealerUserData();
        }
    }, [selectedUser]);

    function resetUpdateUser() {
        setUserData(null);
        setSelectedUser(null);
    }

    useEffect(() => {
        if (isEditMode && !selectedUser) {
            getAllFieldUsers();
        }
        if (!isEditMode && userData) {
            resetUpdateUser();
        }
    }, [isEditMode, userData, selectedUser]);

    return (
        <div className={'field-user-container__body'}>
            {isEditMode && (
                <div className='fieldUserList'>
                    <p>Please select a user to update from the list below:</p>
                    <FieldUserList
                        users={fieldUsers}
                        isLoading={isFieldUsersLoading}
                        selectedUser={selectedUser}
                        setSelectedUser={setSelectedUser}
                    />
                </div>
            )}
            {!isEditMode || (isEditMode && userData && !isLoading) ? (
                <div
                    className={`${
                        isEditMode ? 'bt' : ''
                    } fieldUserFormContainer`}
                >
                    <div className={'field-user-container__body-header'}>
                        {!isEditMode
                            ? 'Add new user'
                            : userData
                            ? userData?.firstName + ' ' + userData?.lastName
                            : ''}
                        {isEditMode && (
                            <div
                                className={`button-container ${
                                    userData?.isUserLocked
                                        ? ''
                                        : 'container-only-child'
                                }`}
                            >
                                <span
                                    className='options-icon'
                                    onClick={() => {
                                        setOpenDrop(true);
                                    }}
                                >
                                    <ProfileOptionsIcon />
                                </span>
                                <Dropdown
                                    dropdown={openDrop}
                                    showSelected={false}
                                    showSelectedIcon={false}
                                    handlerChange={(value: boolean) => {
                                        setOpenDrop(value);
                                    }}
                                >
                                    <MenuItem
                                        deleteHandler={() => {
                                            setModalOpen('DELETE');
                                        }}
                                        sendPasswordResetHandler={
                                            onSendPasswordResetLinkHandler
                                        }
                                        resetMFAHandler={
                                            onSendResetMFALinkHandler
                                        }
                                    />
                                </Dropdown>
                            </div>
                        )}
                    </div>
                    <div className='field-user-container__body-allfields'>
                        <span>*</span>
                        {addNewUser.BODY_ALLFIELDS}
                    </div>
                    <Form
                        setSuccessNotification={setSuccessNotification}
                        setErrorNotification={setErrorNotification}
                        setSuccessNotificationMessage={
                            setSuccessNotificationMessage
                        }
                        setErrorNotificationMessage={
                            setErrorNotificationMessage
                        }
                        userData={userData}
                        regions={regions}
                        districts={districts}
                        isUserActive={isUserActive}
                        isLoadingDistAndReg={isLoadingDistAndReg}
                        isEditMode={isEditMode}
                        onUserUpdate={() => {
                            setUserData(null);
                            getAllFieldUsers();
                        }}
                    />
                    <DialogModal
                        titleContent={
                            modalOpen === 'PASSWORD_RESET'
                                ? dealerMgmt.DEALER_MGMT_RESETPASS_MODAL_TITLE
                                : modalOpen === 'DELETE_MFA'
                                ? dealerMgmt.DEALER_MGMT_DELETEMFA_MODAL_TITLE
                                : `${
                                      dealerMgmt.DEALER_MGMT_DELETE_MODAL_TITLE
                                  } ${
                                      userData?.firstName +
                                      ' ' +
                                      userData?.lastName
                                  }`
                        }
                        bodyContent={
                            modalOpen === 'PASSWORD_RESET'
                                ? dealerMgmt.DEALER_MGMT_RESETPASS_MODAL_DESCRIPTION
                                : modalOpen === 'DELETE_MFA'
                                ? dealerMgmt.DEALER_MGMT_DELETEMFA_MODAL_DESCRIPTION
                                : dealerMgmt.DEALER_MGMT_DELETE_MODAL_DESCRIPTION
                        }
                        open={modalOpen !== ''}
                        cancelBtnLabel={addNewUser.MODAL_LABEL_BUTTON_CANCEL}
                        confirmBtnLabel={
                            modalOpen === 'PASSWORD_RESET'
                                ? dealerMgmt.DEALER_MGMT_RESETPASS_MODAL_CONFIRM_BUTTON
                                : modalOpen === 'DELETE_MFA'
                                ? dealerMgmt.DEALER_MGMT_DELETEMFA_MODAL_CONFIRM_BUTTON
                                : dealerMgmt.DEALER_MGMT_DELETE_MODAL_CONFIRM_BUTTON
                        }
                        onCancelHandler={handleModalCancel}
                        onConfirmHandler={
                            modalOpen === 'PASSWORD_RESET'
                                ? passwordResetConfirm
                                : modalOpen === 'DELETE_MFA'
                                ? deleteMFAConfirm
                                : deleteConfirm
                        }
                        isLoading={modalIsLoading}
                        isConfirmBtnRed={modalOpen === 'DELETE'}
                        keepOpenOnConfirm={
                            modalOpen === 'PASSWORD_RESET' ||
                            modalOpen === 'DELETE_MFA'
                        }
                    />
                </div>
            ) : isEditMode && isLoading ? (
                <div className='bt fieldUserFormContainer'>
                    <div className='loader_container'>
                        <Spinner />
                    </div>
                </div>
            ) : null}
        </div>
    );
};

function mapUserDataResponse(data: any): IUserData {
    const { dsourceProfile, userProfile, regions, districts, ldapLockStatus } =
        data;
    const phone = selectPhoneNumber(dsourceProfile.user.phoneNumbers);

    const user = {
        firstName: dsourceProfile.user.firstName || '',
        lastName: dsourceProfile.user.lastName || '',
        phone,
        email: userProfile.userLoginId,
        partyId: userProfile.userPartyId,
        userId: userProfile.userId,
        userStatus: userProfile.userStatus,
        regions: regions,
        districts: districts,
        isUserLocked: ldapLockStatus.isUserLocked,
    };

    if (!user.email || !user.partyId || !user.userId) {
        throw Error();
    }

    return user;
}

function selectPhoneNumber(
    phoneNumbers: Array<{
        areaCode: string;
        contactNumber: string;
        telecomunicationId: number;
        telecomunicationType: number;
    }>,
): string {
    let phoneNumber = '';
    const EMPTY_ARRAY = 0;

    if (phoneNumbers && phoneNumbers.length > EMPTY_ARRAY) {
        phoneNumber = `${phoneNumbers[0].areaCode}${phoneNumbers[0].contactNumber}`;
    }

    return phoneNumber;
}

function isStatusActive(status: number): boolean {
    const ACTIVE_STATUS_NUMBER = 0;
    return status == ACTIVE_STATUS_NUMBER;
}

export default Body;
